<template>
  <v-container>
    <h1 class="mb-4">Call Analysis Pipeline</h1>
    <v-progress-linear indeterminate v-if="initialLoading" class="mb-4"/>
    
    <!-- <pre class="text-left">{{ summary }}</pre> -->

    <!-- Pipeline Overview Cards -->
    <v-row class="mb-6" v-if="!initialLoading">
      <v-col cols="12" md="3">
        <v-card class="pipeline-card total-card">
          <v-card-text class="text-center">
            <v-icon size="48" color="primary">mdi-phone-incoming</v-icon>
            <div class="text-h4 mt-2">{{ summary.combined.total | formatNumber }}</div>
            <div class="text-subtitle-1">Total Calls</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pipeline-card processing-card">
          <v-card-text class="text-center">
            <v-icon size="48" color="warning">mdi-cog-sync</v-icon>
            <div class="text-h4 mt-2">{{ summary.combined.processing | formatNumber }}</div>
            <div class="text-subtitle-1">Processing</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pipeline-card completed-card">
          <v-card-text class="text-center">
            <v-icon size="48" color="success">mdi-check-circle</v-icon>
            <div class="text-h4 mt-2">{{ summary.combined.completed | formatNumber }}</div>
            <div class="text-subtitle-1">Completed</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pipeline-card error-card">
          <v-card-text class="text-center">
            <v-icon size="48" color="error">mdi-alert-circle</v-icon>
            <div class="text-h4 mt-2">{{ summary.combined.errors | formatNumber }}</div>
            <div class="text-subtitle-1">Errors</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ---------------------- Import ---------------------- -->
    <!-- Data Ingestion (Passive vs Manual) -->
    <v-expansion-panels class="mb-6" v-if="!initialLoading">
      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">
          <div>
            Step 1: Import Data
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card-text>
            <v-sheet color="grey lighten-4 mb-4 pa-4" rounded>
              <div class="d-flex align-center mt-2">
                <v-icon size="18" class="mr-2">mdi-link-variant</v-icon>
                <code class="width-100">https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/processCallData</code>
              </div>
              <div class="text-caption grey--text mt-2">
                Snowflake should POST a JSON array to this endpoint. Each row is written to a staging table which triggers the pipeline.
              </div>
            </v-sheet>
            <v-sheet color="grey lighten-4 mb-4" rounded>
              <div class="d-flex align-center flex-wrap">
                <v-chip class="mr-2 mb-2" color="warning" text-color="white" small>Processing: {{ summary.import.processing | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="success" text-color="white" small>Completed: {{ summary.import.completed | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="error" text-color="white" small>Errors: {{ summary.import.errors | formatNumber }}</v-chip>
                <v-spacer />
                <v-btn class="mr-2" color="primary" :loading="runningBackfill" @click="runBackfill">Run Data Transformation</v-btn>
              </div>
            </v-sheet>
            <div class="mt-2">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="text-subtitle-1 font-weight-medium">Imports in the last 24 hours</div>
                <div class="text-caption grey--text">Updated {{ lastUpdated | timeAgo }}</div>
              </div>
              <v-chart v-if="importChartOptions" class="chart" :option="importChartOptions" autoresize />
              <v-alert v-else type="info" dense outlined class="mt-2">No imports in the last 24 hours</v-alert>
            </div>
          </v-card-text>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- Step 2: Download -->
      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Step 2: Download Recordings from Snowflake</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card-text>
            <v-sheet color="grey lighten-4 mb-4" rounded>
              <div class="d-flex align-center flex-wrap">
                <v-chip class="mr-2 mb-2" color="info" text-color="white" small>Ready: {{ summary.download.ready | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="warning" text-color="white" small>Processing: {{ summary.download.processing | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="success" text-color="white" small>Completed: {{ summary.download.completed | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="error" text-color="white" small>Errors: {{ summary.download.errors | formatNumber }}</v-chip>
                <v-spacer />
                <v-btn class="mr-2" color="primary" :loading="runningDownload" @click="runDownload">Run Download</v-btn>
                <v-btn v-if="summary.download.processing > 0" color="error" :loading="runningResetDownload" @click="handleResetDownload">Reset Processing</v-btn>
              </div>
            </v-sheet>

            <div class="mt-2">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="text-subtitle-1 font-weight-medium">Downloads in the last 24 hours</div>
                <div class="text-caption grey--text">Updated {{ lastDownloadUpdated | timeAgo }}</div>
              </div>
              <v-chart v-if="downloadChartOptions" class="chart" :option="downloadChartOptions" autoresize />
              <v-alert v-else type="info" dense outlined class="mt-2">No downloads in the last 24 hours</v-alert>
            </div>
          </v-card-text>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- Step 3: Convert -->
      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Step 3: Convert Recordings</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card-text>
            <v-sheet color="grey lighten-4 mb-4" rounded>
              <div class="d-flex align-center flex-wrap">
                <v-chip class="mr-2 mb-2" color="info" text-color="white" small>Ready: {{ summary.convert.ready | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="warning" text-color="white" small>Processing: {{ summary.convert.processing | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="success" text-color="white" small>Completed: {{ summary.convert.completed | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="error" text-color="white" small>Errors: {{ summary.convert.errors | formatNumber }}</v-chip>
                <v-spacer />
                <v-btn class="mr-2" color="primary" :loading="runningConvert" @click="runConvert">Run Convert</v-btn>
                <v-btn v-if="summary.convert.processing > 0" class="mr-2" :loading="loadingConvertProcessing" @click="openConvertProcessingDialog">View Processing IDs</v-btn>
                <v-btn v-if="summary.convert.errors > 0" class="mr-2" :loading="loadingConvertError" @click="openConvertErrorDialog">View Error IDs</v-btn>
                <v-btn v-if="summary.convert.processing > 0" color="error" :loading="runningResetConvert" @click="handleResetConvert">Reset Processing</v-btn>
              </div>
            </v-sheet>
            <v-dialog v-model="showConvertProcessingDialog" max-width="800px">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <div>Conversion: Processing IDs ({{ convertProcessing.length | formatNumber }})</div>
                  <v-spacer />
                  <v-btn small class="mr-2" :loading="loadingConvertProcessing" @click="loadConvertProcessingIds">Refresh</v-btn>
                  <v-btn small class="mr-2" @click="copyConvertProcessingIds">Copy All</v-btn>
                  <v-btn icon @click="showConvertProcessingDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                  <v-progress-linear indeterminate v-if="loadingConvertProcessing" />
                  <v-alert v-if="!loadingConvertProcessing && convertProcessing.length === 0" type="info" dense outlined>No conversion tasks are processing.</v-alert>
                  <v-list v-else two-line>
                    <v-list-item v-for="row in convertProcessing" :key="row.id">
                      <v-list-item-content>
                        <v-list-item-title><code>{{ row.id }}</code></v-list-item-title>
                        <v-list-item-subtitle>updated {{ row.updatedAt | timeAgo }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn icon small @click="copyText(row.id)"><v-icon small>mdi-content-copy</v-icon></v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn text @click="showConvertProcessingDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog v-model="showConvertErrorDialog" max-width="800px">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <div>Conversion: Error IDs ({{ convertErrors.length | formatNumber }})</div>
                  <v-spacer />
                  <v-btn small class="mr-2" :loading="loadingConvertError" @click="loadConvertErrorIds">Refresh</v-btn>
                  <v-btn icon @click="showConvertErrorDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                  <v-progress-linear indeterminate v-if="loadingConvertError" />
                  <v-alert v-if="!loadingConvertError && convertErrors.length === 0" type="info" dense outlined>No conversion errors found.</v-alert>
                  <v-list v-else two-line>
                    <v-list-item v-for="row in convertErrors" :key="row.id">
                      <v-list-item-content>
                        <v-list-item-title><code>{{ row.id }}</code></v-list-item-title>
                        <v-list-item-subtitle>updated {{ row.updatedAt | timeAgo }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn text @click="showConvertErrorDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <div class="mt-2">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="text-subtitle-1 font-weight-medium">Conversions in the last 24 hours</div>
                <div class="text-caption grey--text">Updated {{ lastConvertUpdated | timeAgo }}</div>
              </div>
              <v-chart v-if="convertChartOptions" class="chart" :option="convertChartOptions" autoresize />
              <v-alert v-else type="info" dense outlined class="mt-2">No conversions in the last 24 hours</v-alert>
            </div>
          </v-card-text>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- Step 4: Transcribe -->
      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Step 4: Transcribe Recordings</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card-text>
            <v-sheet color="grey lighten-4 mb-4" rounded>
              <div class="d-flex align-center flex-wrap">
                <v-chip class="mr-2 mb-2" color="info" text-color="white" small>Ready: {{ summary.convert.completed | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="warning" text-color="white" small>Processing: {{ summary.transcribe.processing | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="success" text-color="white" small>Completed: {{ summary.transcribe.completed | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="error" text-color="white" small>Errors: {{ summary.transcribe.errors | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" :color="activeBreakers.length ? 'orange darken-2' : 'grey'" text-color="white" small>
                  Breakers: {{ activeBreakers.length }}
                </v-chip>
                <v-chip class="mr-2 mb-2" color="indigo" text-color="white" small>
                  Capacity: {{ currentProcessingTranscribe | formatNumber }}/{{ (maxConcurrent || 50) | formatNumber }}
                </v-chip>
                <v-spacer />
                <v-btn class="mr-2" color="primary" :loading="runningTranscribe" @click="runTranscribe">Run Transcribe</v-btn>
                <v-btn v-if="summary.transcribe.processing > 0" class="mr-2" :loading="loadingTranscribeProcessing" @click="openTranscribeProcessingDialog">View Processing IDs</v-btn>
                <v-btn v-if="summary.transcribe.errors > 0" class="mr-2" :loading="loadingTranscribeError" @click="openTranscribeErrorDialog">View Error IDs</v-btn>
                <v-btn v-if="summary.transcribe.processing > 0" color="error" class="mr-2" :loading="runningResetTranscribe" @click="handleResetTranscribe">Reset Processing</v-btn>
                <v-btn v-if="summary.transcribe.errors > 0" color="error" :loading="runningResetTranscribeErrors" @click="handleResetTranscribeErrors">Reset Errors</v-btn>
                <v-btn class="ml-2 mr-2" :loading="runningResetBreakers" @click="handleResetTranscriptionBreakers">Reset Breakers</v-btn>
                <!-- <v-btn class="ml-2" color="secondary" :loading="runningFixTranscriptionFields" @click="fixTranscriptionFieldNaming">Fix Transcription Field Names</v-btn> -->
                <!-- <v-btn :loading="loadingTranscribeRuntime" @click="loadTranscribeRuntime">Reload Runtime</v-btn> -->
              </div>
            </v-sheet>
            <v-dialog v-model="showTranscribeProcessingDialog" max-width="800px">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <div>Transcription: Processing IDs ({{ transcribeProcessing.length | formatNumber }})</div>
                  <v-spacer />
                  <v-btn small class="mr-2" :loading="loadingTranscribeProcessing" @click="loadTranscribeProcessingIds">Refresh</v-btn>
                  <v-btn small class="mr-2" @click="copyTranscribeProcessingIds">Copy All</v-btn>
                  <v-btn icon @click="showTranscribeProcessingDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                  <v-progress-linear indeterminate v-if="loadingTranscribeProcessing" />
                  <v-alert v-if="!loadingTranscribeProcessing && transcribeProcessing.length === 0" type="info" dense outlined>No transcription tasks are processing.</v-alert>
                  <v-list v-else two-line>
                    <v-list-item v-for="row in transcribeProcessing" :key="row.id">
                      <v-list-item-content>
                        <v-list-item-title><code>{{ row.id }}</code></v-list-item-title>
                        <v-list-item-subtitle>updated {{ row.updatedAt | timeAgo }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn icon small @click="copyText(row.id)"><v-icon small>mdi-content-copy</v-icon></v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn text @click="showTranscribeProcessingDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog v-model="showTranscribeErrorDialog" max-width="800px">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <div>Transcription: Error IDs ({{ transcribeErrors.length | formatNumber }})</div>
                  <v-spacer />
                  <v-btn small class="mr-2" :loading="loadingTranscribeError" @click="loadTranscribeErrorIds">Refresh</v-btn>
                  <v-btn icon @click="showTranscribeErrorDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                  <v-progress-linear indeterminate v-if="loadingTranscribeError" />
                  <v-alert v-if="!loadingTranscribeError && transcribeErrors.length === 0" type="info" dense outlined>No transcription errors found.</v-alert>
                  <v-list v-else two-line>
                    <v-list-item v-for="row in transcribeErrors" :key="row.id">
                      <v-list-item-content>
                        <v-list-item-title><code>{{ row.id }}</code></v-list-item-title>
                        <v-list-item-subtitle>updated {{ row.updatedAt | timeAgo }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn text @click="showTranscribeErrorDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <div class="mt-2">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="text-subtitle-1 font-weight-medium">Transcriptions in the last 24 hours</div>
                <div class="text-caption grey--text">Updated {{ lastTranscribeUpdated | timeAgo }}</div>
              </div>
              <v-chart v-if="transcribeChartOptions" class="chart" :option="transcribeChartOptions" autoresize />
              <v-alert v-else type="info" dense outlined class="mt-2">No transcriptions in the last 24 hours</v-alert>
            </div>

            <v-divider class="my-6"></v-divider>

            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header class="text-h6">Transcription Router Configuration</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-sheet color="grey lighten-4 pa-4" rounded>
                    <v-list>
                      <v-list-item v-for="(p, idx) in config.order" :key="'ord-'+p">
                        <v-list-item-title>{{ p }}</v-list-item-title>
                        <v-list-item-action>
                          <v-btn icon small class="mr-1" :disabled="idx===0" @click="moveInArray(config.order, idx, idx-1)"><v-icon small>mdi-arrow-up</v-icon></v-btn>
                          <v-btn icon small class="mr-1" :disabled="idx===config.order.length-1" @click="moveInArray(config.order, idx, idx+1)"><v-icon small>mdi-arrow-down</v-icon></v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>

                    <div class="pa-4 d-flex align-center justify-end">
                      <v-btn :loading="loadingTranscribeConfig" @click="loadTranscribeConfig">Reload</v-btn>
                      <v-btn color="primary" class="ml-2" :loading="savingConfig" @click="saveTranscribeConfig">Save Config</v-btn>
                    </div>

                  </v-sheet>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

          </v-card-text>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- Step 5: Conversation -->
      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Step 5: Conversation Extraction</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card-text>
            <v-sheet color="grey lighten-4 mb-4" rounded>
              <div class="d-flex align-center flex-wrap">
                <v-chip class="mr-2 mb-2" color="info" text-color="white" small>Ready: {{ summary.transcribe.completed | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="warning" text-color="white" small>Processing: {{ summary.conversation.processing | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="success" text-color="white" small>Completed: {{ summary.conversation.completed | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="error" text-color="white" small>Errors: {{ summary.conversation.errors | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="indigo" text-color="white" small>
                  Capacity: {{ currentProcessingConversation | formatNumber }}/{{ (maxConcurrentConversation || 3) | formatNumber }}
                </v-chip>
                <v-spacer />
                <v-btn class="mr-2" color="primary" :loading="runningConversation" @click="runConversation">Run Conversation Extraction</v-btn>
                <v-btn v-if="summary.conversation.processing > 0" class="mr-2" :loading="loadingConversationProcessing" @click="openConversationProcessingDialog">View Processing IDs</v-btn>
                <v-btn v-if="summary.conversation.errors > 0" class="mr-2" :loading="loadingConversationError" @click="openConversationErrorDialog">View Error IDs</v-btn>
                <v-btn v-if="summary.conversation.errors > 0" color="error" :loading="runningResetConversationErrors" @click="handleResetConversationErrors">Reset Errors</v-btn>
              </div>
            </v-sheet>

            <v-dialog v-model="showConversationProcessingDialog" max-width="800px">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <div>Conversation: Processing IDs ({{ conversationProcessing.length | formatNumber }})</div>
                  <v-spacer />
                  <v-btn small class="mr-2" :loading="loadingConversationProcessing" @click="loadConversationProcessingIds">Refresh</v-btn>
                  <v-btn icon @click="showConversationProcessingDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                  <v-progress-linear indeterminate v-if="loadingConversationProcessing" />
                  <v-alert v-if="!loadingConversationProcessing && conversationProcessing.length === 0" type="info" dense outlined>No conversation tasks are processing.</v-alert>
                  <v-list v-else two-line>
                    <v-list-item v-for="row in conversationProcessing" :key="row.id">
                      <v-list-item-content>
                        <v-list-item-title><code>{{ row.id }}</code></v-list-item-title>
                        <v-list-item-subtitle>updated {{ row.updatedAt | timeAgo }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn text @click="showConversationProcessingDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog v-model="showConversationErrorDialog" max-width="800px">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <div>Conversation: Error IDs ({{ conversationErrors.length | formatNumber }})</div>
                  <v-spacer />
                  <v-btn small class="mr-2" :loading="loadingConversationError" @click="loadConversationErrorIds">Refresh</v-btn>
                  <v-btn icon @click="showConversationErrorDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                  <v-progress-linear indeterminate v-if="loadingConversationError" />
                  <v-alert v-if="!loadingConversationError && conversationErrors.length === 0" type="info" dense outlined>No conversation errors found.</v-alert>
                  <v-list v-else two-line>
                    <v-list-item v-for="row in conversationErrors" :key="row.id">
                      <v-list-item-content>
                        <v-list-item-title><code>{{ row.id }}</code></v-list-item-title>
                        <v-list-item-subtitle>updated {{ row.updatedAt | timeAgo }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn text @click="showConversationErrorDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <div class="mt-2">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="text-subtitle-1 font-weight-medium">Conversations in the last 24 hours</div>
                <div class="text-caption grey--text">Updated {{ lastConversationUpdated | timeAgo }}</div>
              </div>
              <v-chart v-if="conversationChartOptions" class="chart" :option="conversationChartOptions" autoresize />
              <v-alert v-else type="info" dense outlined class="mt-2">No conversations extracted in the last 24 hours</v-alert>
            </div>
          </v-card-text>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- Step 6: Summarization -->
      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Step 6: Summarization</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card-text>
            <v-sheet color="grey lighten-4 mb-4" rounded>
              <div class="d-flex align-center flex-wrap">
                <v-chip class="mr-2 mb-2" color="info" text-color="white" small>Ready: {{ summary.summarization.ready | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="warning" text-color="white" small>Processing: {{ summary.summarization.processing | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="success" text-color="white" small>Completed: {{ summary.summarization.completed | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="error" text-color="white" small>Errors: {{ summary.summarization.errors | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="indigo" text-color="white" small>
                  Capacity: {{ currentProcessingSummarize | formatNumber }}/{{ (maxConcurrentSummarize || 3) | formatNumber }}
                </v-chip>
                <v-spacer />
                <v-btn class="mr-2" color="primary" :loading="runningSummarize" @click="runSummarizeStep6">Run Summarization</v-btn>
                <!-- <v-text-field v-model="testSummarizeCallId" dense outlined hide-details placeholder="callId" style="max-width: 220px" class="mr-2" /> -->
                <!-- <v-btn :disabled="!testSummarizeCallId" color="secondary" class="mr-2" @click="handleTestSummarize">Test One</v-btn> -->
                <v-btn v-if="summary.summarization.processing > 0" class="mr-2" :loading="loadingSummarizeProcessing" @click="openSummarizeProcessingDialog">View Processing IDs</v-btn>
                <v-btn v-if="summary.summarization.errors > 0" class="mr-2" :loading="loadingSummarizeError" @click="openSummarizeErrorDialog">View Error IDs</v-btn>
                <v-btn v-if="summary.summarization.processing > 0" color="error" :loading="runningResetSummarizeStuck" @click="handleResetStuckSummarize">Reset Processing</v-btn>
                <v-btn v-if="summary.summarization.errors > 0" color="error" :loading="runningResetSummarizeErrors" @click="handleResetSummarizeErrors">Reset Errors</v-btn>
              </div>
            </v-sheet>

            <v-dialog v-model="showSummarizeProcessingDialog" max-width="800px">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <div>Summarization: Processing IDs ({{ summarizeProcessing.length | formatNumber }})</div>
                  <v-spacer />
                  <v-btn small class="mr-2" :loading="loadingSummarizeProcessing" @click="loadSummarizeProcessingIds">Refresh</v-btn>
                  <v-btn icon @click="showSummarizeProcessingDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                  <v-progress-linear indeterminate v-if="loadingSummarizeProcessing" />
                  <v-alert v-if="!loadingSummarizeProcessing && summarizeProcessing.length === 0" type="info" dense outlined>No summarization tasks are processing.</v-alert>
                  <v-list v-else two-line>
                    <v-list-item v-for="row in summarizeProcessing" :key="row.id">
                      <v-list-item-content>
                        <v-list-item-title><code>{{ row.id }}</code></v-list-item-title>
                        <v-list-item-subtitle>updated {{ row.updatedAt | timeAgo }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn icon small @click="copyText(row.id)"><v-icon small>mdi-content-copy</v-icon></v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn text @click="showSummarizeProcessingDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-dialog v-model="showSummarizeErrorDialog" max-width="800px">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <div>Summarization: Error IDs ({{ summarizeErrors.length | formatNumber }})</div>
                  <v-spacer />
                  <v-btn small class="mr-2" :loading="loadingSummarizeError" @click="loadSummarizeErrorIds">Refresh</v-btn>
                  <v-btn icon @click="showSummarizeErrorDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                  <v-progress-linear indeterminate v-if="loadingSummarizeError" />
                  <v-alert v-if="!loadingSummarizeError && summarizeErrors.length === 0" type="info" dense outlined>No summarization errors found.</v-alert>
                  <v-list v-else two-line>
                    <v-list-item v-for="row in summarizeErrors" :key="row.id">
                      <v-list-item-content>
                        <v-list-item-title><code>{{ row.id }}</code></v-list-item-title>
                        <v-list-item-subtitle>updated {{ row.updatedAt | timeAgo }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn text @click="showSummarizeErrorDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <div class="mt-2">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="text-subtitle-1 font-weight-medium">Summaries in the last 24 hours</div>
                <div class="text-caption grey--text">Updated {{ lastSummarizeUpdated | timeAgo }}</div>
              </div>
              <v-chart v-if="summarizeChartOptions" class="chart" :option="summarizeChartOptions" autoresize />
              <v-alert v-else type="info" dense outlined class="mt-2">No summaries created in the last 24 hours</v-alert>
            </div>
          </v-card-text>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- Step 7: Reasoning -->
      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Step 7: Reasoning</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card-text>
            <v-sheet color="grey lighten-4 mb-4" rounded>
              <div class="d-flex align-center flex-wrap">
                <!-- <v-text-field v-model="reasoningCallId" dense outlined hide-details placeholder="callId (optional)" style="max-width: 280px" class="mr-2" />
                <v-btn class="mr-2" color="primary" :loading="runningFanout" @click="runReasoningFanout">Run Fan-out</v-btn> -->
                <v-btn class="mr-2" color="primary" :loading="runningReasoningBatch" @click="runReasoningBatch">Run Processing Batch</v-btn>
                <v-spacer />
                <v-chip class="mr-2 mb-2" color="info" text-color="white" small>Ready: {{ summary.summarization?.completed | formatNumber }}</v-chip>
                <v-chip class="mr-2 mb-2" color="success" text-color="white" small>Completed: {{ summary.combined.completed | formatNumber }}</v-chip>
              </div>
            </v-sheet>

            <v-simple-table dense>
              <thead>
                <tr>
                  <th class="text-left">Substep</th>
                  <th class="text-left">Ready</th>
                  <th class="text-left">Processing</th>
                  <th class="text-left">Completed</th>
                  <th class="text-left">Errors</th>
                  <th class="text-left">Capacity</th>
                  <th class="text-left">Enabled</th>
                  <th class="text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in reasoningRows" :key="row.key">
                  <td>{{ row.label }}</td>
                  <td><v-chip small color="info" text-color="white">{{ row.ready | formatNumber }}</v-chip></td>
                  <td><v-chip small color="warning" text-color="white">{{ row.processing | formatNumber }}</v-chip></td>
                  <td><v-chip small color="success" text-color="white">{{ row.completed | formatNumber }}</v-chip></td>
                  <td><v-chip small color="error" text-color="white">{{ row.errors | formatNumber }}</v-chip></td>
                  <td>
                    <v-chip small color="indigo" text-color="white">
                      {{ row.currentProcessing | formatNumber }}/{{ (row.maxConcurrent || 0) | formatNumber }}
                    </v-chip>
                  </td>
                  <td>
                    <v-switch inset hide-details color="primary" :input-value="row.enabled" @change="toggleReasoning(row.key, $event)"></v-switch>
                  </td>
                  <td>
                    <!-- <v-btn x-small class="mr-2" @click="runOneSubstep(row.key)">Run One</v-btn> -->
                    <v-btn v-if="row.processing > 0" x-small class="mr-2" @click="openSubstepProcessing(row.key)">Processing IDs</v-btn>
                    <v-btn v-if="row.errors > 0" x-small class="mr-2" @click="openSubstepErrors(row.key)">Error IDs</v-btn>
                    <v-btn
                      x-small
                      class="mr-2"
                      color="primary"
                      :loading="!!reasoningRunReady[row.key]"
                      :disabled="!!reasoningRunReady[row.key]"
                      @click="runReady(row.key)"
                    >
                      Run Ready
                    </v-btn>
                    <v-btn
                      v-if="row.processing > 0"
                      x-small
                      color="error"
                      class="mr-2"
                      :loading="!!reasoningResetProcessing[row.key]"
                      :disabled="!!reasoningResetProcessing[row.key]"
                      @click="resetSubstep(row.key, 'processing')"
                    >
                      Reset Processing
                    </v-btn>
                    <v-btn
                      v-if="row.errors > 0"
                      x-small
                      color="error"
                      :loading="!!reasoningResetErrors[row.key]"
                      :disabled="!!reasoningResetErrors[row.key]"
                      @click="resetSubstep(row.key, 'failed')"
                    >
                      Reset Errors
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <!-- Reasoning dialogs -->
            <v-dialog v-model="showReasoningProcessingDialog" max-width="800px">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <div>{{ reasoningDialogKey }}: Processing IDs ({{ reasoningProcessing.length | formatNumber }})</div>
                  <v-spacer />
                  <v-btn small class="mr-2" :loading="loadingReasoningProcessing" @click="openSubstepProcessing(reasoningDialogKey)">Refresh</v-btn>
                  <v-btn icon @click="showReasoningProcessingDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                  <v-progress-linear indeterminate v-if="loadingReasoningProcessing" />
                  <v-alert v-if="!loadingReasoningProcessing && reasoningProcessing.length === 0" type="info" dense outlined>No tasks processing.</v-alert>
                  <v-list v-else two-line>
                    <v-list-item v-for="row in reasoningProcessing" :key="row.id">
                      <v-list-item-content>
                        <v-list-item-title><code>{{ row.id }}</code></v-list-item-title>
                        <v-list-item-subtitle>updated {{ row.updatedAt | timeAgo }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn text @click="showReasoningProcessingDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-dialog v-model="showReasoningErrorDialog" max-width="800px">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <div>{{ reasoningDialogKey }}: Error IDs ({{ reasoningErrors.length | formatNumber }})</div>
                  <v-spacer />
                  <v-btn small class="mr-2" :loading="loadingReasoningError" @click="openSubstepErrors(reasoningDialogKey)">Refresh</v-btn>
                  <v-btn icon @click="showReasoningErrorDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                  <v-progress-linear indeterminate v-if="loadingReasoningError" />
                  <v-alert v-if="!loadingReasoningError && reasoningErrors.length === 0" type="info" dense outlined>No errors found.</v-alert>
                  <v-list v-else two-line>
                    <v-list-item v-for="row in reasoningErrors" :key="row.id">
                      <v-list-item-content>
                        <v-list-item-title><code>{{ row.id }}</code></v-list-item-title>
                        <v-list-item-subtitle>updated {{ row.updatedAt | timeAgo }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn text @click="showReasoningErrorDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <div class="mt-4">
              <div class="text-subtitle-1 font-weight-medium mb-2">Completed Calls in the last 24 hours</div>
              <v-chart v-if="completedChartOptions" class="chart" :option="completedChartOptions" autoresize />
              <v-alert v-else type="info" dense outlined class="mt-2">No completed calls in the last 24 hours</v-alert>
            </div>
          </v-card-text>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- Step 8: Daily Aggregation -->
      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Step 8: Daily Aggregation</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card-text>
            
            <div class="d-flex align-center mb-4">
              <v-menu
                v-model="dailyMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dailyRangeLabel"
                    label="Date Range"
                    prepend-icon="mdi-calendar"
                    readonly
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    style="max-width: 280px"
                  />
                </template>
                <v-date-picker
                  v-model="dailyRange"
                  range
                  scrollable
                  @change="applyDailyRange"
                />
              </v-menu>
              <v-switch
                v-model="dailyRangeMarkComplete"
                class="ml-4"
                label="Mark Complete"
                inset
                hide-details
              />
              <v-btn class="ml-4" :disabled="!dailyRangeValid" :loading="runningDaily" color="primary" @click="runDailyRange()">Run Range</v-btn>
            </div>
            <div>
              <div class="text-subtitle-2 mb-2">Latest result</div>
              <pre class="grey lighten-4 pa-3" style="white-space: pre-wrap">{{ lastDailyResult }}</pre>
            </div>
          </v-card-text>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

  </v-container>
</template>

<script>
import { getStatusSummary, triggerImport, triggerDownload, resetDownload, triggerConvert, resetConvert } from '@/firebase/functions'
import { getFirestore, collection, query, where, getDocs, getCountFromServer } from 'firebase/firestore'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart, BarChart } from 'echarts/charts'
import { TitleComponent, TooltipComponent, GridComponent, LegendComponent } from 'echarts/components'
import VChart from 'vue-echarts'
import { callAnalysisFunctions } from '@/firebase/functions'

use([CanvasRenderer, LineChart, BarChart, TitleComponent, TooltipComponent, GridComponent, LegendComponent])
export default {
  name: 'CallAnalysisPipeline',
  components: { VChart },
  data() {
    return {
      initialLoading: true,
      loading: false,
      summary: {
        combined: {
          processing: 0,
          completed: 0,
          errors: 0,
          total: 0,
        },
        import: {
          ready: 0, // cc_callanalysis_data - cc_callanalysis_calls
          processing: 0, // status: import_processing
          completed: 0, // status: import_completed
          errors: 0, // status: import_failed
          total: 0, // ready + processing + completed + errors
        },
        download: {
          ready: 0, // status: import_completed
          processing: 0, // status: download_processing
          completed: 0, // status: download_completed
          errors: 0, // status: download_failed
          total: 0, // ready + processing + completed + errors
        },
        convert: {
          ready: 0, // status: download_completed
          processing: 0, // status: conversion_processing
          completed: 0, // status: conversion_completed  
          errors: 0, // status: conversion_failed
          total: 0, // ready + processing + completed + errors
        },
        transcribe: {
          ready: 0, // status: transcription_ready
          processing: 0, // status: transcription_processing
          completed: 0, // status: transcription_completed
          errors: 0, // status: transcription_failed
          total: 0, // status: transcription_completed
        },
        conversation: {
          ready: 0, // status: transcription_completed
          processing: 0, // status: conversation_processing
          completed: 0, // status: conversation_completed
          errors: 0, // status: conversation_failed
          total: 0, // ready + processing + completed + errors
        },
        reason: {
          // Summariazation
          // Categorization
          // Threads
          // Sentiment
          // Satisfaction
          // Opportunities
          // Journeys
          summarization: {
            ready: 0, // status: conversation_completed
            processing: 0, // status: reasoning_summarization_processing
            completed: 0, // status: reasoning_summarization_completed
            errors: 0, // status: reasoning_summarization_failed
            total: 0, // ready + processing + completed + errors
          },
          categorization: {
            ready: 0, // status: reasoning_summarization_completed
            processing: 0, // status: reasoning_categorization_processing
            completed: 0, // status: reasoning_categorization_completed
            errors: 0, // status: reasoning_categorization_failed
            total: 0, // ready + processing + completed + errors
          },
          threads: {    
            ready: 0, // status: reasoning_categorization_completed
            processing: 0, // status: reasoning_threads_processing
            completed: 0, // status: reasoning_threads_completed
            errors: 0, // status: reasoning_threads_failed
            total: 0, // ready + processing + completed + errors
          },
          sentiment: {
            ready: 0, // status: reasoning_threads_completed
            processing: 0, // status: reasoning_sentiment_processing
            completed: 0, // status: reasoning_sentiment_completed
            errors: 0, // status: reasoning_sentiment_failed
            total: 0, // ready + processing + completed + errors
          },
          satisfaction: {
            ready: 0, // status: reasoning_sentiment_completed
            processing: 0, // status: reasoning_satisfaction_processing
            completed: 0, // status: reasoning_satisfaction_completed
            errors: 0, // status: reasoning_satisfaction_failed
            total: 0, // ready + processing + completed + errors
          },
          opportunities: {
            ready: 0, // status: reasoning_satisfaction_completed or reasoning_threads_completed
            processing: 0, // status: reasoning_opportunities_processing
            completed: 0, // status: reasoning_opportunities_completed
            errors: 0, // status: reasoning_opportunities_failed
            total: 0, // ready + processing + completed + errors
          },
          journeys: {
            ready: 0, // status: reasoning_opportunities_completed or reasoning_sentiment_completed
            processing: 0, // status: reasoning_journeys_processing
            completed: 0, // status: reasoning_journeys_completed
            errors: 0, // status: reasoning_journeys_failed
            total: 0, // ready + processing + completed + errors
          },
        },
        complete: {
          total: 0,
          ready: 0,
          processing: 0,
          completed: 0,
          errors: 0,
        },
        daily: {
          total: 0,
          ready: 0,
          processing: 0,
          completed: 0,
          errors: 0,
        },
      },
      importChartOptions: null,
      downloadChartOptions: null,
      convertChartOptions: null,
      transcribeChartOptions: null,
      conversationChartOptions: null,
      summarizeChartOptions: null,
      lastUpdated: null,
      runningBackfill: false,
      runningDownload: false,
      runningResetDownload: false,
      runningConvert: false,
      runningResetConvert: false,
      lastDownloadUpdated: null
      , lastConvertUpdated: null
      , lastTranscribeUpdated: null
      , lastConversationUpdated: null
      , lastSummarizeUpdated: null
      , runningTranscribe: false
      , showSummarizeProcessingDialog: false
      , loadingSummarizeProcessing: false
      , summarizeProcessing: []
      , showSummarizeErrorDialog: false
      , loadingSummarizeError: false
      , summarizeErrors: []
      , runningSummarize: false
      , testSummarizeCallId: ''
      , savingConfig: false
      , loadingTranscribeConfig: false
      , showConvertProcessingDialog: false
      , loadingConvertProcessing: false
      , convertProcessing: []
      , showConvertErrorDialog: false
      , loadingConvertError: false
      , convertErrors: []
      , showTranscribeProcessingDialog: false
      , loadingTranscribeProcessing: false
      , transcribeProcessing: []
      , showTranscribeErrorDialog: false
      , loadingTranscribeError: false
      , transcribeErrors: []
      , runningResetTranscribe: false
      , runningResetTranscribeErrors: false
      , runningResetBreakers: false
      , runningFixTranscriptionFields: false
      , runningResetSummarizeStuck: false
      , runningResetSummarizeErrors: false
      , loadingTranscribeRuntime: false
      , maxConcurrent: null
      , currentProcessingTranscribe: 0
      , breakers: {}
      , activeBreakers: []
      , currentProcessingSummarize: 0
      , maxConcurrentSummarize: null
      , runningConversation: false
      , runningResetConversationErrors: false
      , showConversationProcessingDialog: false
      , loadingConversationProcessing: false
      , conversationProcessing: []
      , showConversationErrorDialog: false
      , loadingConversationError: false
      , conversationErrors: []
      , loadingConversationRuntime: false
      , currentProcessingConversation: 0
      , maxConcurrentConversation: null
      , config: {
        order: ["streaming", "multimodal", "chirp", "whisper", "diarization"]
        , enabled: {}
      }
      , reasoningCallId: ''
      , runningFanout: false
      , runningReasoningBatch: false
      , completedChartOptions: null
      , showReasoningProcessingDialog: false
      , loadingReasoningProcessing: false
      , reasoningProcessing: []
      , showReasoningErrorDialog: false
      , loadingReasoningError: false
      , reasoningErrors: []
      , reasoningDialogKey: ''
      , reasoningRows: [
        { key: 'categorization', label: 'Categorization', ready: 0, processing: 0, completed: 0, errors: 0, currentProcessing: 0, maxConcurrent: null, enabled: true },
        { key: 'threads',        label: 'Threads',        ready: 0, processing: 0, completed: 0, errors: 0, currentProcessing: 0, maxConcurrent: null, enabled: true },
        { key: 'sentiment',      label: 'Sentiment',      ready: 0, processing: 0, completed: 0, errors: 0, currentProcessing: 0, maxConcurrent: null, enabled: true },
        { key: 'satisfaction',   label: 'Satisfaction',   ready: 0, processing: 0, completed: 0, errors: 0, currentProcessing: 0, maxConcurrent: null, enabled: true },
        { key: 'opportunities',  label: 'Opportunities',  ready: 0, processing: 0, completed: 0, errors: 0, currentProcessing: 0, maxConcurrent: null, enabled: true },
        { key: 'journeys',       label: 'Journeys',       ready: 0, processing: 0, completed: 0, errors: 0, currentProcessing: 0, maxConcurrent: null, enabled: true },
      ]
      , reasoningResetProcessing: {}
      , reasoningResetErrors: {}
      , reasoningEnabled: {}
      , runningDaily: false
      
      , lastDailyResult: ''
      , dailyMenu: false
      , dailyRange: []
      , dailyRangeLabel: ''
      , dailyRangeMarkComplete: false
      , showReasoningErrorDialog: false
      , loadingReasoningError: false
      , reasoningProcessing: []
      , reasoningErrors: []
      , reasoningDialogKey: ''
      , reasoningRunReady: {}
    }
  },
  computed: {
    providerKeys() {
      const orderKeys = Array.isArray(this.config.order) ? this.config.order : []
      const enabledKeys = this.config.enabled ? Object.keys(this.config.enabled) : []
      const defaults = ["streaming", "multimodal", "chirp", "whisper", "diarization"]
      return Array.from(new Set([...orderKeys, ...enabledKeys, ...defaults]))
    },
    dailyRangeValid() {
      return Array.isArray(this.dailyRange) && this.dailyRange.length === 2 && this.dailyRange[0] && this.dailyRange[1]
    }
  },
  mounted() {
    // Always use backend summary endpoint for full picture (not limited to 100 docs)
    this.fetchStatus()
    this.refreshImportChart()
    this.refreshDownloadChart()
    this.refreshConvertChart()
    this.refreshTranscribeChart()
    this.refreshConversationChart()
    this.refreshSummarizeChart()
    this.loadTranscribeConfig()
    this.loadTranscribeRuntime()
    this.loadConversationRuntime()
    this.refreshCompletedChart()
    this.loadReasoningRuntime()
  },
  methods: {
    async fixTranscriptionFieldNaming() {
      try {
        this.runningFixTranscriptionFields = true
        const { getFirestore, collection, getDocs, query, where, limit, startAfter, updateDoc, doc, deleteField } = await import('firebase/firestore')
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')

        let processed = 0
        let fixes = 0
        let lastDoc = null
        const pageSize = 500

        // Page through only conversation_failed records for faster, focused fix
        while (true) {
          const q = lastDoc
            ? query(callsCol, where('status', '==', 'conversation_failed'), limit(pageSize), startAfter(lastDoc))
            : query(callsCol, where('status', '==', 'conversation_failed'), limit(pageSize))
          const snap = await getDocs(q)
          if (snap.empty) break

          for (const d of snap.docs) {
            processed++
            const data = d.data() || {}
            const transcription = data.transcription
            const existingTranscriptions = (data.transcriptions && typeof data.transcriptions === 'object') ? data.transcriptions : null

            // Skip if no legacy field present
            if (transcription == null) {
              continue
            }

            // Determine best text + method from a variety of legacy shapes
            let legacyText = ''
            let legacyMethod = ''

            if (typeof transcription === 'string') {
              legacyText = transcription
              legacyMethod = 'legacy'
            } else if (typeof transcription === 'object') {
              // Common legacy keys we have seen
              legacyText = transcription.enhanced || transcription.combined || transcription.full_text || transcription.text || transcription.transcript || ''
              legacyMethod = transcription.method || 'legacy'
            }

            // If we already have an enhanced/combined transcript, avoid clobbering
            const alreadyHasTranscript = Boolean(existingTranscriptions?.enhanced || existingTranscriptions?.combined)

            const payload = {}
            let wrote = false

            if (!alreadyHasTranscript && typeof legacyText === 'string' && legacyText.trim().length > 0) {
              // Prefer writing to enhanced; fall back to combined if needed later
              payload['transcriptions.enhanced'] = legacyText
              wrote = true
            }
            if (typeof legacyMethod === 'string' && legacyMethod.trim().length > 0 && !existingTranscriptions?.method) {
              payload['transcriptions.method'] = legacyMethod
              wrote = true
            }

            // Always delete legacy container to normalize the schema
            payload['transcription'] = deleteField()

            // Only send an update when there is something to change
            if (wrote || transcription !== undefined) {
              await updateDoc(doc(db, 'cc_callanalysis_calls', d.id), payload)
              if (wrote) fixes++
            }
          }
          lastDoc = snap.docs[snap.docs.length - 1]
        }

        this.showSnackbar(`Field rename completed. Processed: ${processed}, Fixed: ${fixes}`)
      } catch (e) {
        console.error('Fix transcription field naming failed:', e)
        this.showSnackbar('Fix transcription fields failed', 'error')
      } finally {
        this.runningFixTranscriptionFields = false
      }
    },
    async runSummarizeStep6() {
      try {
        this.runningSummarize = true
        const { triggerSummarizeStep6 } = await import('@/firebase/functions')
        const res = await triggerSummarizeStep6()
        const dispatched = res?.data?.processed ?? res?.data?.count ?? res?.processed ?? res?.count
        this.showSnackbar(dispatched != null ? `Summarization dispatched: ${dispatched}` : 'Summarization triggered')
        await this.fetchStatus()
        await this.refreshSummarizeChart()
      } catch (e) {
        console.error('Summarization trigger failed:', e)
        this.showSnackbar('Summarization trigger failed', 'error')
      } finally {
        this.runningSummarize = false
      }
    },
    async loadSummarizeRuntime() {
      try {
        const { getFirestore, doc, getDoc, collection, where, query, getCountFromServer } = await import('firebase/firestore')
        const db = getFirestore()
        // Max concurrency
        const cfgSnap = await getDoc(doc(db, 'cc_runtime/summarization_config'))
        const cfg = cfgSnap.exists() ? (cfgSnap.data() || {}) : {}
        const max = parseInt(cfg.max_concurrent_summarizations, 10)
        this.maxConcurrentSummarize = Number.isFinite(max) && max > 0 ? max : 3
        // Current processing count
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const countSnap = await getCountFromServer(query(callsCol, where('status', '==', 'summarization_processing')))
        this.currentProcessingSummarize = countSnap.data().count || 0
      } catch (e) {
        console.warn('Failed to load summarization runtime:', e)
      }
    },
    async openSummarizeProcessingDialog() {
      this.showSummarizeProcessingDialog = true
      await this.loadSummarizeProcessingIds()
    },
    async loadSummarizeProcessingIds() {
      try {
        this.loadingSummarizeProcessing = true
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const snap = await getDocs(query(callsCol, where('status', '==', 'summarization_processing')))
        this.summarizeProcessing = snap.docs.map(d => ({ id: d.id, updatedAt: d.get('updatedAt')?.toDate?.() || null }))
      } catch (e) {
        console.error('Failed to load summarize processing IDs:', e)
        this.summarizeProcessing = []
      } finally {
        this.loadingSummarizeProcessing = false
      }
    },
    async openSummarizeErrorDialog() {
      this.showSummarizeErrorDialog = true
      await this.loadSummarizeErrorIds()
    },
    async loadSummarizeErrorIds() {
      try {
        this.loadingSummarizeError = true
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const snap = await getDocs(query(callsCol, where('status', '==', 'summarization_failed')))
        this.summarizeErrors = snap.docs.map(d => ({ id: d.id, updatedAt: d.get('updatedAt')?.toDate?.() || null }))
      } catch (e) {
        console.error('Failed to load summarize error IDs:', e)
        this.summarizeErrors = []
      } finally {
        this.loadingSummarizeError = false
      }
    },
    async handleTestSummarize() {
      try {
        const { testSummarizeStep6 } = await import('@/firebase/functions')
        await testSummarizeStep6(this.testSummarizeCallId)
        this.showSnackbar(`Summarized ${this.testSummarizeCallId}`)
        await this.fetchStatus()
        await this.refreshSummarizeChart()
      } catch (e) {
        console.error('Test summarize failed:', e)
        this.showSnackbar('Test summarize failed', 'error')
      }
    },
    async handleResetSummarizeErrors() {
      try {
        this.runningResetSummarizeErrors = true
        const { resetSummarizeErrors } = await import('@/firebase/functions')
        await resetSummarizeErrors()
        this.showSnackbar('Reset summarization errors triggered')
        await this.fetchStatus()
        await this.refreshSummarizeChart()
      } catch (e) {
        console.error('Reset summarization errors failed:', e)
        this.showSnackbar('Reset summarization errors failed', 'error')
      } finally {
        this.runningResetSummarizeErrors = false
      }
    },
    async handleResetStuckSummarize() {
      try {
        this.runningResetSummarizeStuck = true
        const { resetStuckSummarize } = await import('@/firebase/functions')
        await resetStuckSummarize()
        this.showSnackbar('Reset stuck summarization triggered')
        await this.fetchStatus()
        await this.refreshSummarizeChart()
      } catch (e) {
        console.error('Reset stuck summarization failed:', e)
        this.showSnackbar('Reset stuck summarization failed', 'error')
      } finally {
        this.runningResetSummarizeStuck = false
      }
    },
    async runConversation() {
      try {
        this.runningConversation = true
        const { triggerConversationStep5 } = await import('@/firebase/functions')
        const res = await triggerConversationStep5()
        const dispatched = res?.data?.processed ?? res?.data?.count ?? res?.processed ?? res?.count
        this.showSnackbar(dispatched != null ? `Conversation dispatched: ${dispatched}` : 'Conversation triggered')
        await this.fetchStatus()
        await this.loadConversationRuntime()
      } catch (e) {
        console.error('Conversation trigger failed:', e)
        this.showSnackbar('Conversation trigger failed', 'error')
      } finally {
        this.runningConversation = false
      }
    },
    async handleResetConversationErrors() {
      try {
        this.runningResetConversationErrors = true
        const { resetConversationErrors } = await import('@/firebase/functions')
        await resetConversationErrors()
        this.showSnackbar('Reset conversation errors triggered')
        await this.fetchStatus()
        await this.loadConversationRuntime()
      } catch (e) {
        console.error('Reset conversation errors failed:', e)
        this.showSnackbar('Reset conversation errors failed', 'error')
      } finally {
        this.runningResetConversationErrors = false
      }
    },
    async openConversationProcessingDialog() {
      this.showConversationProcessingDialog = true
      await this.loadConversationProcessingIds()
    },
    async loadConversationProcessingIds() {
      try {
        this.loadingConversationProcessing = true
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const snap = await getDocs(query(callsCol, where('status', '==', 'conversation_processing')))
        this.conversationProcessing = snap.docs.map(d => ({ id: d.id, updatedAt: d.get('updatedAt')?.toDate?.() || null }))
      } catch (e) {
        console.error('Failed to load conversation processing IDs:', e)
        this.conversationProcessing = []
      } finally {
        this.loadingConversationProcessing = false
      }
    },
    async openConversationErrorDialog() {
      this.showConversationErrorDialog = true
      await this.loadConversationErrorIds()
    },
    async loadConversationErrorIds() {
      try {
        this.loadingConversationError = true
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const snap = await getDocs(query(callsCol, where('status', '==', 'conversation_failed')))
        this.conversationErrors = snap.docs.map(d => ({ id: d.id, updatedAt: d.get('updatedAt')?.toDate?.() || null }))
      } catch (e) {
        console.error('Failed to load conversation error IDs:', e)
        this.conversationErrors = []
      } finally {
        this.loadingConversationError = false
      }
    },
    async loadConversationRuntime() {
      try {
        this.loadingConversationRuntime = true
        const { getFirestore, doc, getDoc, collection, where, query, getCountFromServer } = await import('firebase/firestore')
        const db = getFirestore()
        // Max concurrency (optional config)
        const cfgSnap = await getDoc(doc(db, 'cc_runtime/conversation_config'))
        const cfg = cfgSnap.exists() ? (cfgSnap.data() || {}) : {}
        const max = parseInt((cfg.max_concurrent_conversations ?? cfg.max_concurrent_extractions), 10)
        this.maxConcurrentConversation = Number.isFinite(max) && max > 0 ? max : 3
        // Current processing count
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const countSnap = await getCountFromServer(query(callsCol, where('status', '==', 'conversation_processing')))
        this.currentProcessingConversation = countSnap.data().count || 0
      } catch (e) {
        console.warn('Failed to load conversation runtime:', e)
      } finally {
        this.loadingConversationRuntime = false
      }
    },
    async runTranscribe() {
      try {
        this.runningTranscribe = true
        const { triggerTranscribe } = await import('@/firebase/functions')
        const res = await triggerTranscribe()
        const dispatched = res?.data?.dispatched ?? res?.data?.count ?? res?.dispatched ?? res?.count
        const capacity = res?.data?.capacity
        const maxConcurrent = res?.data?.maxConcurrent
        if (capacity === 0) {
          const msg = (typeof maxConcurrent === 'number')
            ? `No capacity available (${this.currentProcessingTranscribe}/${maxConcurrent})`
            : 'No capacity available'
          this.showSnackbar(msg, 'warning')
        } else {
          this.showSnackbar(dispatched != null ? `Transcribe dispatched: ${dispatched}` : 'Transcribe triggered')
        }
        await this.fetchStatus()
        await this.refreshTranscribeChart()
        await this.loadTranscribeRuntime()
      } catch (e) {
        console.error('Transcribe trigger failed:', e)
        this.showSnackbar('Transcribe trigger failed', 'error')
      } finally {
        this.runningTranscribe = false
      }
    },
    async openTranscribeProcessingDialog() {
      this.showTranscribeProcessingDialog = true
      await this.loadTranscribeProcessingIds()
    },
    async loadTranscribeProcessingIds() {
      try {
        this.loadingTranscribeProcessing = true
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const snap = await getDocs(query(callsCol, where('status', '==', 'transcription_processing')))
        this.transcribeProcessing = snap.docs.map(d => ({ id: d.id, updatedAt: d.get('updatedAt')?.toDate?.() || null }))
      } catch (e) {
        console.error('Failed to load transcribe processing IDs:', e)
        this.transcribeProcessing = []
      } finally {
        this.loadingTranscribeProcessing = false
      }
    },
    async openTranscribeErrorDialog() {
      this.showTranscribeErrorDialog = true
      await this.loadTranscribeErrorIds()
    },
    async loadTranscribeErrorIds() {
      try {
        this.loadingTranscribeError = true
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const snap = await getDocs(query(callsCol, where('status', '==', 'transcription_failed')))
        this.transcribeErrors = snap.docs.map(d => ({ id: d.id, updatedAt: d.get('updatedAt')?.toDate?.() || null }))
      } catch (e) {
        console.error('Failed to load transcribe error IDs:', e)
        this.transcribeErrors = []
      } finally {
        this.loadingTranscribeError = false
      }
    },
    async copyTranscribeProcessingIds() {
      try {
        const text = this.transcribeProcessing.map(r => r.id).join('\n')
        await navigator.clipboard.writeText(text)
        this.showSnackbar('IDs copied to clipboard')
      } catch (e) {
        this.showSnackbar('Copy failed', 'error')
      }
    },
    async handleResetTranscribe() {
      try {
        this.runningResetTranscribe = true
        const { resetTranscribe } = await import('@/firebase/functions')
        await resetTranscribe()
        this.showSnackbar('Reset transcribe triggered')
        await this.fetchStatus()
        await this.refreshTranscribeChart()
        await this.loadTranscribeRuntime()
      } catch (e) {
        console.error('Reset transcribe failed:', e)
        this.showSnackbar('Reset transcribe failed', 'error')
      } finally {
        this.runningResetTranscribe = false
      }
    },
    async handleResetTranscribeErrors() {
      try {
        this.runningResetTranscribeErrors = true
        const { resetTranscribeErrors } = await import('@/firebase/functions')
        await resetTranscribeErrors()
        this.showSnackbar('Reset transcription errors triggered')
        await this.fetchStatus()
        await this.refreshTranscribeChart()
        await this.loadTranscribeRuntime()
      } catch (e) {
        console.error('Reset transcription errors failed:', e)
        this.showSnackbar('Reset transcription errors failed', 'error')
      } finally {
        this.runningResetTranscribeErrors = false
      }
    },
    async handleResetTranscriptionBreakers() {
      try {
        this.runningResetBreakers = true
        const { resetTranscriptionBreakers } = await import('@/firebase/functions')
        await resetTranscriptionBreakers()
        this.showSnackbar('Transcription breakers cleared')
        await this.fetchStatus()
        await this.refreshTranscribeChart()
        await this.loadTranscribeRuntime()
      } catch (e) {
        console.error('Reset transcription breakers failed:', e)
        this.showSnackbar('Reset transcription breakers failed', 'error')
      } finally {
        this.runningResetBreakers = false
      }
    },
    async loadTranscribeRuntime() {
      try {
        this.loadingTranscribeRuntime = true
        const { getFirestore, doc, getDoc, collection, where, query, getCountFromServer } = await import('firebase/firestore')
        const db = getFirestore()
        // Breakers
        const breakersSnap = await getDoc(doc(db, 'cc_runtime/transcription_breakers'))
        const breakers = breakersSnap.exists() ? (breakersSnap.data() || {}) : {}
        const now = Date.now()
        const active = []
        Object.entries(breakers).forEach(([k, until]) => {
          if (typeof until === 'number' && until > now) active.push(k)
        })
        this.breakers = breakers
        this.activeBreakers = active
        // Max concurrency
        const cfgSnap = await getDoc(doc(db, 'cc_runtime/transcription_config'))
        const cfg = cfgSnap.exists() ? (cfgSnap.data() || {}) : {}
        const max = parseInt(cfg.max_concurrent_transcriptions, 10)
        this.maxConcurrent = Number.isFinite(max) && max > 0 ? max : 50
        // Current processing count (approximate)
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const countSnap = await getCountFromServer(query(callsCol, where('status', '==', 'transcription_processing')))
        this.currentProcessingTranscribe = countSnap.data().count || 0
      } catch (e) {
        console.warn('Failed to load transcription runtime:', e)
      } finally {
        this.loadingTranscribeRuntime = false
      }
    },
    async loadTranscribeConfig() {
      try {
        this.loadingTranscribeConfig = true
        const { getFirestore, doc, getDoc } = await import('firebase/firestore')
        const db = getFirestore()
        const ref = doc(db, 'cc_runtime/transcription_config')
        const snap = await getDoc(ref)
        if (snap.exists()) {
          const data = snap.data()
          this.config.order = Array.isArray(data.order) ? data.order : (Array.isArray(data.standard_order) ? data.standard_order : this.config.order)
          this.config.enabled = Object.assign({}, this.config.enabled, data.enabled || {})
        }
      } catch (e) {
        console.warn('Failed to load transcription config:', e)
      } finally {
        this.loadingTranscribeConfig = false
      }
    },
    async saveTranscribeConfig() {
      try {
        this.savingConfig = true
        const { getFirestore, doc, setDoc } = await import('firebase/firestore')
        const db = getFirestore()
        const ref = doc(db, 'cc_runtime/transcription_config')
        await setDoc(ref, JSON.parse(JSON.stringify(this.config)), { merge: true })
        this.showSnackbar('Transcription config saved')
      } catch (e) {
        console.error('Failed to save transcription config:', e)
        this.showSnackbar('Failed to save config', 'error')
      } finally {
        this.savingConfig = false
      }
    },
    moveInArray(arr, fromIdx, toIdx) {
      if (toIdx < 0 || toIdx >= arr.length) return
      const [item] = arr.splice(fromIdx, 1)
      arr.splice(toIdx, 0, item)
    },
    setEnabled(key, val) {
      this.config.enabled = Object.assign({}, this.config.enabled, { [key]: !!val })
    },
    addToOrder(key) {
      if (!this.config.order.includes(key)) this.config.order.push(key)
    },
    async fetchStatus() {
      this.loading = true
      try {
        const res = await getStatusSummary()
        console.log('Status summary:', res.data)
        // Reset overall counters
        this.summary.combined.processing = 0
        this.summary.combined.completed = res.data?.completed || 0
        this.summary.combined.errors = res.data?.error || 0
        // All other counts (excluding completed/error keys) contribute to processing
        for (const key in res.data) {
          if (key !== 'completed' && key !== 'error') {
            this.summary.combined.processing += res.data[key]
          }
        }
        this.summary.combined.total = this.summary.combined.completed + this.summary.combined.errors + this.summary.combined.processing

        // Step 1: Import summary (source of truth):
        // ready = #cc_callanalysis_data - #cc_callanalysis_calls
        const db = getFirestore()
        const [dataCountSnap, callsCountSnap] = await Promise.all([
          getCountFromServer(collection(db, 'cc_callanalysis_data')),
          getCountFromServer(collection(db, 'cc_callanalysis_calls')),
        ])        

        console.log('Data count:', dataCountSnap.data().count)
        console.log('Calls count:', callsCountSnap.data().count)

        const dataCount = dataCountSnap.data().count || 0
        const callsCount = callsCountSnap.data().count || 0
        this.summary.import.ready = Math.max(0, dataCount - callsCount)
        this.summary.import.processing = res.data?.import_processing || 0
        this.summary.import.completed = res.data?.import_completed || 0
        this.summary.import.errors = res.data?.import_failed || 0
        this.summary.import.total = this.summary.import.ready + this.summary.import.processing + this.summary.import.completed + this.summary.import.errors
        // Step 2: Download summary (source of truth):
        this.summary.download.ready = res.data?.import_completed || 0
        this.summary.download.processing = res.data?.download_processing || 0
        this.summary.download.completed = res.data?.download_completed || 0
        this.summary.download.errors = res.data?.download_failed || 0
        this.summary.download.total = this.summary.download.ready + this.summary.download.processing + this.summary.download.completed + this.summary.download.errors
        // Step 3: Convert summary (source of truth):
        this.summary.convert.ready = res.data?.download_completed || 0
        this.summary.convert.processing = res.data?.conversion_processing || 0
        this.summary.convert.completed = res.data?.conversion_completed || 0
        this.summary.convert.errors = res.data?.conversion_failed || 0
        this.summary.convert.total = this.summary.convert.ready + this.summary.convert.processing + this.summary.convert.completed + this.summary.convert.errors
        // Step 4: Transcribe summary (source of truth):
        this.summary.transcribe.ready = res.data?.transcription_ready || 0
        this.summary.transcribe.processing = res.data?.transcription_processing || 0
        this.summary.transcribe.completed = res.data?.transcription_completed || 0
        this.summary.transcribe.errors = res.data?.transcription_failed || 0
        this.summary.transcribe.total = this.summary.transcribe.ready + this.summary.transcribe.processing + this.summary.transcribe.completed + this.summary.transcribe.errors

        // Step 5: Conversation summary (derive from status counts)
        this.summary.conversation.ready = res.data?.transcription_completed || 0
        this.summary.conversation.processing = (res.data?.conversation_processing || 0)
        this.summary.conversation.completed = (res.data?.conversation_completed || 0)
        this.summary.conversation.errors = (res.data?.conversation_failed || 0)
        this.summary.conversation.total = this.summary.conversation.ready + this.summary.conversation.processing + this.summary.conversation.completed + this.summary.conversation.errors
        
        // Step 6: Summarization summary (derive from status counts)
        this.summary.summarization = this.summary.summarization || { ready: 0, processing: 0, completed: 0, errors: 0, total: 0 }
        this.summary.summarization.ready = res.data?.conversation_completed || 0
        this.summary.summarization.processing = (res.data?.summarization_processing || 0)
        this.summary.summarization.completed = (res.data?.summarized || 0)
        this.summary.summarization.errors = (res.data?.summarization_failed || 0)
        this.summary.summarization.total = this.summary.summarization.ready + this.summary.summarization.processing + this.summary.summarization.completed + this.summary.summarization.errors

      } catch (e) {
        console.error('Failed to fetch status summary:', e)
        // this.summary = { error: e.message }
      } finally {
        this.loading = false
        if (this.initialLoading) this.initialLoading = false
      }
    },
    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color,
        timeout: 5000
      }
    },
    
    async runBackfill() {
      try {
        this.runningBackfill = true
        await triggerImport()
        this.showSnackbar('Backfill started')
        await this.refreshImportChart()
      } catch (e) {
        console.error('Backfill failed:', e)
        this.showSnackbar('Backfill failed', 'error')
      } finally {
        this.runningBackfill = false
      }
    },
    async runDownload() {
      try {
        this.runningDownload = true
        const res = await triggerDownload()
        const dispatched = res?.data?.dispatched ?? res?.data?.count ?? res?.dispatched ?? res?.count
        this.showSnackbar(dispatched != null ? `Download dispatched: ${dispatched}` : 'Download triggered')
        await this.fetchStatus()
        await this.refreshDownloadChart()
      } catch (e) {
        console.error('Download trigger failed:', e)
        this.showSnackbar('Download trigger failed', 'error')
      } finally {
        this.runningDownload = false
      }
    },
    async handleResetDownload() {
      try {
        this.runningResetDownload = true
        await resetDownload()
        this.showSnackbar('Reset triggered')
        await this.fetchStatus()
        await this.refreshDownloadChart()
      } catch (e) {
        console.error('Reset download failed:', e)
        this.showSnackbar('Reset download failed', 'error')
      } finally {
        this.runningResetDownload = false
      }
    },
    async runConvert() {
      try {
        this.runningConvert = true
        const res = await triggerConvert()
        const dispatched = res?.data?.dispatched ?? res?.data?.count ?? res?.dispatched ?? res?.count
        this.showSnackbar(dispatched != null ? `Convert dispatched: ${dispatched}` : 'Convert triggered')
        await this.fetchStatus()
        await this.refreshConvertChart()
      } catch (e) {
        console.error('Convert trigger failed:', e)
        this.showSnackbar('Convert trigger failed', 'error')
      } finally {
        this.runningConvert = false
      }
    },
    async openConvertProcessingDialog() {
      this.showConvertProcessingDialog = true
      await this.loadConvertProcessingIds()
    },
    async loadConvertProcessingIds() {
      try {
        this.loadingConvertProcessing = true
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const snap = await getDocs(query(callsCol, where('status', '==', 'conversion_processing')))
        this.convertProcessing = snap.docs.map(d => ({ id: d.id, updatedAt: d.get('updatedAt')?.toDate?.() || null }))
      } catch (e) {
        console.error('Failed to load processing IDs:', e)
        this.convertProcessing = []
      } finally {
        this.loadingConvertProcessing = false
      }
    },
    async copyConvertProcessingIds() {
      try {
        const text = this.convertProcessing.map(r => r.id).join('\n')
        await navigator.clipboard.writeText(text)
        this.showSnackbar('IDs copied to clipboard')
      } catch (e) {
        this.showSnackbar('Copy failed', 'error')
      }
    },
    async openConvertErrorDialog() {
      this.showConvertErrorDialog = true
      await this.loadConvertErrorIds()
    },
    async loadConvertErrorIds() {
      try {
        this.loadingConvertError = true
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const snap = await getDocs(query(callsCol, where('status', '==', 'conversion_failed')))
        this.convertErrors = snap.docs.map(d => ({ id: d.id, updatedAt: d.get('updatedAt')?.toDate?.() || null }))
      } catch (e) {
        console.error('Failed to load convert error IDs:', e)
        this.convertErrors = []
      } finally {
        this.loadingConvertError = false
      }
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.showSnackbar('Copied')
      } catch (e) {
        this.showSnackbar('Copy failed', 'error')
      }
    },
    async handleResetConvert() {
      try {
        this.runningResetConvert = true
        await resetConvert()
        this.showSnackbar('Reset convert triggered')
        await this.fetchStatus()
        await this.refreshConvertChart()
      } catch (e) {
        console.error('Reset convert failed:', e)
        this.showSnackbar('Reset convert failed', 'error')
      } finally {
        this.runningResetConvert = false
      }
    },
    async refreshDownloadChart() {
      try {
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const nowMs = Date.now()
        const sinceMs = nowMs - 24 * 60 * 60 * 1000
        // Use immutable completion timestamps where possible so counts persist
        const sinceDate = new Date(sinceMs)
        const [byHistorySnap, byPipelineSnap, byStatusSnap, byConvertHistorySnap] = await Promise.all([
          getDocs(query(callsCol, where('statusHistory.download_completed', '>=', sinceDate))),
          getDocs(query(callsCol, where('pipeline.downloaded', '>=', Math.floor(sinceMs / 1000)))),
          // Fallbacks for records completed before immutable fields existed
          getDocs(query(callsCol, where('status', '==', 'download_completed'))),
          getDocs(query(callsCol, where('statusHistory.conversion_processing', '>=', sinceDate)))
        ])

        const buckets = new Array(24).fill(0)
        const seen = new Set()
        const addTs = (tsMs) => {
          if (!tsMs || tsMs < sinceMs) return
          const diffSec = Math.floor((nowMs - tsMs) / 1000)
          if (diffSec < 0 || diffSec > 24 * 3600) return
          const hoursAgo = Math.floor(diffSec / 3600)
          const index = 23 - Math.min(23, Math.max(0, hoursAgo))
          buckets[index]++
        }
        byHistorySnap.forEach(d => {
          const id = d.id
          if (seen.has(id)) return
          seen.add(id)
          const ts = d.get('statusHistory')?.download_completed?.toDate?.()?.getTime?.() || 0
          addTs(ts)
        })
        byPipelineSnap.forEach(d => {
          const id = d.id
          if (seen.has(id)) return
          seen.add(id)
          const sec = Number(d.get('pipeline')?.downloaded || 0)
          const ts = sec ? sec * 1000 : 0
          addTs(ts)
        })
        // Current status fallback using updatedAt
        byStatusSnap.forEach(d => {
          const id = d.id
          if (seen.has(id)) return
          const ts = d.get('updatedAt')?.toDate?.()?.getTime?.() || 0
          if (!ts || ts < sinceMs) return
          seen.add(id)
          addTs(ts)
        })
        // As a last resort, approximate downloads by conversion start time
        byConvertHistorySnap.forEach(d => {
          const id = d.id
          if (seen.has(id)) return
          seen.add(id)
          const ts = d.get('statusHistory')?.conversion_processing?.toDate?.()?.getTime?.() || 0
          addTs(ts)
        })

        const labels = []
        for (let i = 23; i >= 0; i--) {
          const date = new Date(nowMs - i * 3600 * 1000)
          const hh = `${date.getHours()}`.padStart(2, '0')
          labels.push(`${hh}:00`)
        }

        const hasData = buckets.some(v => v > 0)
        this.downloadChartOptions = hasData ? {
          grid: { left: '3%', right: '2%', bottom: '8%', top: '8%', containLabel: true },
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: labels },
          yAxis: { type: 'value', name: 'Files' },
          series: [ { type: 'bar', name: 'Downloads', data: buckets, barWidth: '60%', itemStyle: { color: '#388E3C' } } ]
        } : null
        this.lastDownloadUpdated = new Date()
      } catch (e) {
        console.error('Failed to build download chart:', e)
        this.downloadChartOptions = null
        this.lastDownloadUpdated = new Date()
      }
    },
    async refreshConvertChart() {
      try {
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const nowMs = Date.now()
        const sinceMs = nowMs - 24 * 60 * 60 * 1000
        const sinceDate = new Date(sinceMs)
        // Prefer immutable completion timestamps
        const [byHistorySnap, byPipelineSnap] = await Promise.all([
          getDocs(query(callsCol, where('statusHistory.conversion_completed', '>=', sinceDate))),
          getDocs(query(callsCol, where('pipeline.converted', '>=', Math.floor(sinceMs / 1000))))
        ])

        const buckets = new Array(24).fill(0)
        const seen = new Set()
        const addTs = (tsMs) => {
          if (!tsMs || tsMs < sinceMs) return
          const diffSec = Math.floor((nowMs - tsMs) / 1000)
          if (diffSec < 0 || diffSec > 24 * 3600) return
          const hoursAgo = Math.floor(diffSec / 3600)
          const index = 23 - Math.min(23, Math.max(0, hoursAgo))
          buckets[index]++
        }
        byHistorySnap.forEach(d => {
          const id = d.id
          if (seen.has(id)) return
          seen.add(id)
          const ts = d.get('statusHistory')?.conversion_completed?.toDate?.()?.getTime?.() || 0
          addTs(ts)
        })
        byPipelineSnap.forEach(d => {
          const id = d.id
          if (seen.has(id)) return
          seen.add(id)
          const sec = Number(d.get('pipeline')?.converted || 0)
          const ts = sec ? sec * 1000 : 0
          addTs(ts)
        })

        const labels = []
        for (let i = 23; i >= 0; i--) {
          const date = new Date(nowMs - i * 3600 * 1000)
          const hh = `${date.getHours()}`.padStart(2, '0')
          labels.push(`${hh}:00`)
        }

        const hasData = buckets.some(v => v > 0)
        this.convertChartOptions = hasData ? {
          grid: { left: '3%', right: '2%', bottom: '8%', top: '8%', containLabel: true },
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: labels },
          yAxis: { type: 'value', name: 'Files' },
          series: [ { type: 'bar', name: 'Conversions', data: buckets, barWidth: '60%', itemStyle: { color: '#7B1FA2' } } ]
        } : null
        this.lastConvertUpdated = new Date()
      } catch (e) {
        console.error('Failed to build convert chart:', e)
        this.convertChartOptions = null
        this.lastConvertUpdated = new Date()
      }
    },
    async refreshImportChart() {
      try {
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const nowSec = Math.floor(Date.now() / 1000)
        const sinceSec = nowSec - 24 * 60 * 60
        const q = query(callsCol, where('pipeline.imported', '>=', sinceSec))
        const snap = await getDocs(q)

        const buckets = new Array(24).fill(0)
        snap.forEach(doc => {
          const importedSec = doc.get('pipeline.imported')
          if (!importedSec) return
          const diffSec = nowSec - importedSec
          if (diffSec < 0 || diffSec > 24 * 3600) return
          const hoursAgo = Math.floor(diffSec / 3600)
          const index = 23 - Math.min(23, Math.max(0, hoursAgo))
          buckets[index]++
        })

        const labels = []
        for (let i = 23; i >= 0; i--) {
          const date = new Date((nowSec - i * 3600) * 1000)
          const hh = `${date.getHours()}`.padStart(2, '0')
          labels.push(`${hh}:00`)
        }

        const hasData = buckets.some(v => v > 0)

        this.importChartOptions = hasData ? {
          grid: { left: '3%', right: '2%', bottom: '8%', top: '8%', containLabel: true },
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: labels },
          yAxis: { type: 'value', name: 'Files' },
          series: [
            { type: 'bar', name: 'Imports', data: buckets, barWidth: '60%', itemStyle: { color: '#1976D2' } }
          ]
        } : null

        this.lastUpdated = new Date()
      } catch (e) {
        console.error('Failed to build import chart:', e)
        this.importChartOptions = null
        this.lastUpdated = new Date()
      }
    }
    , async refreshTranscribeChart() {
      try {
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const nowMs = Date.now()
        const sinceMs = nowMs - 24 * 60 * 60 * 1000
        const sinceDate = new Date(sinceMs)
        // Use immutable completion timestamps so counts persist after status changes
        const [byHistorySnap, byPipelineSnap] = await Promise.all([
          getDocs(query(callsCol, where('statusHistory.transcription_completed', '>=', sinceDate))),
          getDocs(query(callsCol, where('pipeline.transcribed', '>=', Math.floor(sinceMs / 1000))))
        ])

        const buckets = new Array(24).fill(0)
        const seen = new Set()
        const addTs = (tsMs) => {
          if (!tsMs || tsMs < sinceMs) return
          const diffSec = Math.floor((nowMs - tsMs) / 1000)
          if (diffSec < 0 || diffSec > 24 * 3600) return
          const hoursAgo = Math.floor(diffSec / 3600)
          const index = 23 - Math.min(23, Math.max(0, hoursAgo))
          buckets[index]++
        }

        byHistorySnap.forEach(d => {
          const id = d.id
          if (seen.has(id)) return
          seen.add(id)
          const ts = d.get('statusHistory')?.transcription_completed?.toDate?.()?.getTime?.() || 0
          addTs(ts)
        })

        byPipelineSnap.forEach(d => {
          const id = d.id
          if (seen.has(id)) return
          seen.add(id)
          const sec = Number(d.get('pipeline')?.transcribed || 0)
          const ts = sec ? sec * 1000 : 0
          addTs(ts)
        })

        const labels = []
        for (let i = 23; i >= 0; i--) {
          const date = new Date(nowMs - i * 3600 * 1000)
          const hh = `${date.getHours()}`.padStart(2, '0')
          labels.push(`${hh}:00`)
        }

        const hasData = buckets.some(v => v > 0)
        this.transcribeChartOptions = hasData ? {
          grid: { left: '3%', right: '2%', bottom: '8%', top: '8%', containLabel: true },
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: labels },
          yAxis: { type: 'value', name: 'Files' },
          series: [ { type: 'bar', name: 'Transcriptions', data: buckets, barWidth: '60%', itemStyle: { color: '#0288D1' } } ]
        } : null
        this.lastTranscribeUpdated = new Date()
      } catch (e) {
        console.error('Failed to build transcribe chart:', e)
        this.transcribeChartOptions = null
        this.lastTranscribeUpdated = new Date()
      }
    }
    , async refreshConversationChart() {
      try {
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const nowMs = Date.now()
        const sinceMs = nowMs - 24 * 60 * 60 * 1000
        const sinceDate = new Date(sinceMs)
        // Prefer immutable completion timestamp so conversations are counted even after status moves on
        const [byHistory, byTopLevel, byStatus] = await Promise.all([
          getDocs(query(callsCol, where('statusHistory.conversation_completed', '>=', sinceDate))),
          getDocs(query(callsCol, where('conversationCompletedAt', '>=', sinceDate))),
          // Fallback: current status still conversation_completed
          getDocs(query(callsCol, where('status', '==', 'conversation_completed'))),
        ])

        const buckets = new Array(24).fill(0)
        const seen = new Set()
        const addTs = (tsMs, id) => {
          if (!tsMs || tsMs < sinceMs) return
          if (seen.has(id)) return
          const diffSec = Math.floor((nowMs - tsMs) / 1000)
          if (diffSec < 0 || diffSec > 24 * 3600) return
          const hoursAgo = Math.floor(diffSec / 3600)
          const index = 23 - Math.min(23, Math.max(0, hoursAgo))
          buckets[index]++
          seen.add(id)
        }
        byHistory.forEach(d => {
          const ts = d.get('statusHistory')?.conversation_completed?.toDate?.()?.getTime?.() || 0
          addTs(ts, d.id)
        })
        byTopLevel.forEach(d => {
          const ts = d.get('conversationCompletedAt')?.toDate?.()?.getTime?.() || 0
          addTs(ts, d.id)
        })
        byStatus.forEach(d => {
          const ts = d.get('updatedAt')?.toDate?.()?.getTime?.() || 0
          addTs(ts, d.id)
        })

        const labels = []
        for (let i = 23; i >= 0; i--) {
          const date = new Date(nowMs - i * 3600 * 1000)
          const hh = `${date.getHours()}`.padStart(2, '0')
          labels.push(`${hh}:00`)
        }

        const hasData = buckets.some(v => v > 0)
        this.conversationChartOptions = hasData ? {
          grid: { left: '3%', right: '2%', bottom: '8%', top: '8%', containLabel: true },
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: labels },
          yAxis: { type: 'value', name: 'Files' },
          series: [ { type: 'bar', name: 'Conversations', data: buckets, barWidth: '60%', itemStyle: { color: '#00897B' } } ]
        } : null
        this.lastConversationUpdated = new Date()
      } catch (e) {
        console.error('Failed to build conversation chart:', e)
        this.conversationChartOptions = null
        this.lastConversationUpdated = new Date()
      }
    }
    , async refreshSummarizeChart() {
      try {
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const nowMs = Date.now()
        const sinceMs = nowMs - 24 * 60 * 60 * 1000
        const sinceDate = new Date(sinceMs)
        // Prefer immutable completion timestamp so summaries are counted even after status moves on
        const byHistory = await getDocs(query(callsCol, where('statusHistory.summarized', '>=', sinceDate)))
        // Fallback: current status still summarized
        const byStatus = await getDocs(query(callsCol, where('status', '==', 'summarized')))

        const buckets = new Array(24).fill(0)
        const seen = new Set()
        const addTs = (tsMs, id) => {
          if (!tsMs || tsMs < sinceMs) return
          if (seen.has(id)) return
          const diffSec = Math.floor((nowMs - tsMs) / 1000)
          if (diffSec < 0 || diffSec > 24 * 3600) return
          const hoursAgo = Math.floor(diffSec / 3600)
          const index = 23 - Math.min(23, Math.max(0, hoursAgo))
          buckets[index]++
          seen.add(id)
        }
        byHistory.forEach(d => {
          const ts = d.get('statusHistory')?.summarized?.toDate?.()?.getTime?.() || 0
          addTs(ts, d.id)
        })
        byStatus.forEach(d => {
          const ts = d.get('updatedAt')?.toDate?.()?.getTime?.() || 0
          addTs(ts, d.id)
        })

        const labels = []
        for (let i = 23; i >= 0; i--) {
          const date = new Date(nowMs - i * 3600 * 1000)
          const hh = `${date.getHours()}`.padStart(2, '0')
          labels.push(`${hh}:00`)
        }

        const hasData = buckets.some(v => v > 0)
        this.summarizeChartOptions = hasData ? {
          grid: { left: '3%', right: '2%', bottom: '8%', top: '8%', containLabel: true },
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: labels },
          yAxis: { type: 'value', name: 'Files' },
          series: [ { type: 'bar', name: 'Summaries', data: buckets, barWidth: '60%', itemStyle: { color: '#5C6BC0' } } ]
        } : null
        this.lastSummarizeUpdated = new Date()
      } catch (e) {
        console.error('Failed to refresh summarize chart:', e)
        this.summarizeChartOptions = null
        this.lastSummarizeUpdated = new Date()
      }
    }
    , async refreshCompletedChart() {
      try {
        const { getFirestore, collection, query, where, getDocs } = await import('firebase/firestore')
        const db = getFirestore()
        const nowMs = Date.now()
        const sinceMs = nowMs - 24 * 60 * 60 * 1000
        const sinceDate = new Date(sinceMs)
        // Only fetch recent completions (requires composite index on status+updatedAt)
        const snap = await getDocs(query(
          collection(db, 'cc_callanalysis_calls'),
          where('status', '==', 'completed'),
          where('updatedAt', '>=', sinceDate)
        ))

        const buckets = new Array(24).fill(0)
        snap.forEach(d => {
          const ts = d.get('statusHistory')?.completed?.toDate?.()?.getTime?.() || d.get('updatedAt')?.toDate?.()?.getTime?.() || 0
          if (!ts || ts < sinceMs) return
          const diffSec = Math.floor((nowMs - ts) / 1000)
          if (diffSec < 0 || diffSec > 24 * 3600) return
          const hoursAgo = Math.floor(diffSec / 3600)
          const index = 23 - Math.min(23, Math.max(0, hoursAgo))
          buckets[index]++
        })
        const labels = []
        for (let i = 23; i >= 0; i--) {
          const date = new Date(nowMs - i * 3600 * 1000)
          const hh = `${date.getHours()}`.padStart(2, '0')
          labels.push(`${hh}:00`)
        }
        const hasData = buckets.some(v => v > 0)
        this.completedChartOptions = hasData ? {
          grid: { left: '3%', right: '2%', bottom: '8%', top: '8%', containLabel: true },
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: labels },
          yAxis: { type: 'value', name: 'Calls' },
          series: [ { type: 'bar', name: 'Completed', data: buckets, barWidth: '60%', itemStyle: { color: '#2E7D32' } } ]
        } : null
      } catch (e) {
        console.error('Failed to build completed chart:', e)
        this.completedChartOptions = null
      }
    }
    , async loadReasoningRuntime() {
      try {
        const { getFirestore, doc, getDoc, collection, query, where, getCountFromServer } = await import('firebase/firestore')
        const db = getFirestore()
        const cfgSnap = await getDoc(doc(db, 'cc_runtime/reasoning_config'))
        const cfg = cfgSnap.exists() ? (cfgSnap.data() || {}) : {}
        this.reasoningEnabled = Object.assign({ categorization: true, threads: true, sentiment: true, satisfaction: true, opportunities: true, journeys: true }, cfg.enabled || {})
        const limits = cfg.limits || {}

        const callsCol = collection(db, 'cc_callanalysis_calls')
        const substeps = [
          { key: 'categorization', label: 'Categorization' },
          { key: 'threads',        label: 'Threads' },
          { key: 'sentiment',      label: 'Sentiment' },
          { key: 'satisfaction',   label: 'Satisfaction' },
          { key: 'opportunities',  label: 'Opportunities' },
          { key: 'journeys',       label: 'Journeys' },
        ]

        const rows = []
        for (const s of substeps) {
          const field = `reasoning.${s.key}`
          const [readySnap, procSnap, compSnap, failSnap] = await Promise.all([
            getCountFromServer(query(callsCol, where(field, '==', 'ready'))),
            getCountFromServer(query(callsCol, where(field, '==', 'processing'))),
            getCountFromServer(query(callsCol, where(field, '==', 'completed'))),
            getCountFromServer(query(callsCol, where(field, '==', 'failed'))),
          ])
          const ready = readySnap.data().count || 0
          const processing = procSnap.data().count || 0
          const completed = compSnap.data().count || 0
          const errors = failSnap.data().count || 0
          const maxConcurrent = limits?.[s.key]?.maxConcurrent || null
          rows.push({
            key: s.key,
            label: s.label,
            ready,
            processing,
            completed,
            errors,
            currentProcessing: processing,
            maxConcurrent,
            enabled: !!this.reasoningEnabled[s.key]
          })
        }
        this.reasoningRows = rows
      } catch (e) {
        console.warn('Failed to load reasoning runtime:', e)
        this.reasoningRows = []
      }
    }
    , async runReasoningFanout() {
      try {
        this.runningFanout = true
        const { triggerReasoningFanout } = await import('@/firebase/functions')
        const res = await triggerReasoningFanout(this.reasoningCallId || undefined)
        const queued = res?.data?.queued || []
        this.showSnackbar(queued.length ? `Fan-out queued: ${queued.join(', ')}` : 'Fan-out processed')
        await this.fetchStatus()
        await this.loadReasoningRuntime()
        await this.refreshCompletedChart()
      } catch (e) {
        console.error('Fan-out failed:', e)
        this.showSnackbar('Fan-out failed', 'error')
      } finally {
        this.runningFanout = false
      }
    }
    , async runReasoningBatch() {
      try {
        this.runningReasoningBatch = true
        const { triggerReasoningBatch } = await import('@/firebase/functions')
        const res = await triggerReasoningBatch(10, { dryRun: false })
        const queued = res?.data?.queued ?? res?.queued ?? []
        const processed = res?.data?.processed ?? res?.processed
        if (Array.isArray(queued)) {
          this.showSnackbar(queued.length ? `Batch queued ${queued.length}` : 'No summarized records found', queued.length ? 'success' : 'info')
        } else if (typeof processed === 'number') {
          this.showSnackbar(`Batch processed: ${processed}`)
        } else {
          this.showSnackbar('Batch request sent')
        }
        await this.fetchStatus()
        await this.loadReasoningRuntime()
        await this.refreshCompletedChart()
      } catch (e) {
        console.error('Batch fan-out failed:', e)
        this.showSnackbar('Batch fan-out failed', 'error')
      } finally {
        this.runningReasoningBatch = false
      }
    }
    , async toggleReasoning(key, val) {
      try {
        const { getFirestore, doc, setDoc } = await import('firebase/firestore')
        const db = getFirestore()
        await setDoc(doc(db, 'cc_runtime/reasoning_config'), { enabled: { [key]: !!val } }, { merge: true })
        this.reasoningEnabled[key] = !!val
        this.showSnackbar(`${key} ${val ? 'enabled' : 'disabled'}`)
      } catch (e) {
        console.error('Failed to toggle reasoning substep:', e)
        this.showSnackbar('Toggle failed', 'error')
      }
    }
    , async runOneSubstep(key) {
      try {
        const api = await import('@/firebase/functions')
        const map = {
          categorization: 'runCategorization',
          threads: 'runThreads',
          sentiment: 'runSentiment',
          satisfaction: 'runSatisfaction',
          opportunities: 'runOpportunities',
          journeys: 'runJourneys'
        }
        const fnName = map[key]
        if (!fnName || typeof api[fnName] !== 'function') {
          this.showSnackbar('Endpoint not available', 'error')
          return
        }
        await api[fnName]()
        this.showSnackbar(`${key} dispatched`)
        await this.fetchStatus()
        await this.loadReasoningRuntime()
        await this.refreshCompletedChart()
      } catch (e) {
        console.error('Run one substep failed:', e)
        this.showSnackbar('Run one failed', 'error')
      }
    }
    , async openSubstepProcessing(key) {
      try {
        this.reasoningDialogKey = key
        this.showReasoningProcessingDialog = true
        this.loadingReasoningProcessing = true
        const { getFirestore, collection, query, where, getDocs } = await import('firebase/firestore')
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const snap = await getDocs(query(callsCol, where(`reasoning.${key}`, '==', 'processing')))
        this.reasoningProcessing = snap.docs.map(d => ({ id: d.id, updatedAt: d.get('updatedAt')?.toDate?.() || null }))
      } catch (e) {
        console.error('Failed to load processing ids:', e)
        this.reasoningProcessing = []
        this.showSnackbar('Load processing IDs failed', 'error')
      } finally {
        this.loadingReasoningProcessing = false
      }
    }
    , async openSubstepErrors(key) {
      try {
        this.reasoningDialogKey = key
        this.showReasoningErrorDialog = true
        this.loadingReasoningError = true
        const { getFirestore, collection, query, where, getDocs } = await import('firebase/firestore')
        const db = getFirestore()
        const callsCol = collection(db, 'cc_callanalysis_calls')
        const snap = await getDocs(query(callsCol, where(`reasoning.${key}`, '==', 'failed')))
        this.reasoningErrors = snap.docs.map(d => ({ id: d.id, updatedAt: d.get('updatedAt')?.toDate?.() || null }))
      } catch (e) {
        console.error('Failed to load error ids:', e)
        this.reasoningErrors = []
        this.showSnackbar('Load error IDs failed', 'error')
      } finally {
        this.loadingReasoningError = false
      }
    }
    , async resetSubstep(key, state) {
      try {
        const { resetReasoningSubstep } = await import('@/firebase/functions')
        if (state === 'processing') this.$set(this.reasoningResetProcessing, key, true)
        if (state === 'failed') this.$set(this.reasoningResetErrors, key, true)
        const res = await resetReasoningSubstep(key, state)
        const n = res?.data?.processed ?? res?.processed ?? 0
        this.showSnackbar(`${key}: reset ${n} to ready`)
        await this.loadReasoningRuntime()
        await this.refreshCompletedChart()
      } catch (e) {
        console.error('Reset substep failed:', e)
        this.showSnackbar('Reset failed', 'error')
      } finally {
        if (state === 'processing') this.$delete(this.reasoningResetProcessing, key)
        if (state === 'failed') this.$delete(this.reasoningResetErrors, key)
      }
    },
    
    applyDailyRange(val) {
      if (Array.isArray(val) && val.length === 2 && val[0] && val[1]) {
        const [start, end] = [...val].sort()
        this.dailyRange = [start, end]
        this.dailyRangeLabel = `${start} → ${end}`
        this.dailyMenu = false
      }
    },
    async runDailyRange() {
      if (!this.dailyRangeValid) return
      try {
        this.runningDaily = true
        const [rawStart, rawEnd] = this.dailyRange
        const toYMD = (d) => {
          if (!d) return ''
          if (typeof d === 'string') return d.slice(0, 10)
          if (d instanceof Date) return d.toISOString().slice(0, 10)
          try { return new Date(d).toISOString().slice(0, 10) } catch (_) { return '' }
        }
        const startDate = toYMD(rawStart)
        const endDate = toYMD(rawEnd)
        const res = await callAnalysisFunctions.triggerDailyAggregation({ startDate, endDate, markComplete: !!this.dailyRangeMarkComplete })
        this.lastDailyResult = JSON.stringify(res?.data || res, null, 2)
        this.showSnackbar(`Daily aggregation (range) triggered`)
      } catch (e) {
        console.error('Daily range aggregation failed:', e)
        this.showSnackbar('Daily range aggregation failed', 'error')
      } finally {
        this.runningDaily = false
      }
    },
    async runReady(key) {
      try {
        this.$set(this.reasoningRunReady, key, true)
        const { runReasoningReady } = await import('@/firebase/functions')
        const res = await runReasoningReady(key, 20)
        const queued = res?.data?.queued ?? res?.queued ?? 0
        const processed = res?.data?.processed ?? res?.processed ?? 0
        if (processed) {
          this.showSnackbar(`${key}: processed ${processed} ready items`)
        } else {
          this.showSnackbar(`${key}: queued ${queued} ready items`)
        }
        await this.loadReasoningRuntime()
        await this.refreshCompletedChart()
      } catch (e) {
        console.error('Run ready failed:', e)
        this.showSnackbar('Run ready failed', 'error')
      } finally {
        this.$delete(this.reasoningRunReady, key)
      }
    }
  },
  filters: {
    formatNumber(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('en-US')
    },
    timeAgo(value) {
      if (!value) return 'just now'
      const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000)
      if (seconds < 60) return `${seconds}s ago`
      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) return `${minutes}m ago`
      const hours = Math.floor(minutes / 60)
      return `${hours}h ago`
    }
  }
}
</script>

<style scoped>
.chart {
  height: 220px;
}
.v-list {
  background-color: #f5f5f5;
}
.v-list-item {
  border: 1px solid #ccc;
  border-bottom: none;
  background-color: white;
}
.v-list-item:last-child {
  border-bottom: 1px solid #ccc;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}
.v-list-item:first-child {
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}
</style>